import React, { useState, useEffect } from 'react';
import { Button, Checkbox, ClearableInput, Spinner } from '@rio-cloud/rio-uikit';
import { Consultant, ConsultantFormData } from '../../../../models/consultant';

interface ConsultantFormProps {
  consultant?: Consultant | null;
  editConsultant: (id: string, data: ConsultantFormData) => Promise<boolean>;
  addConsultant: (data: ConsultantFormData) => Promise<boolean>;
  onSuccess: () => void;
  onCancel: () => void;
}

const ConsultantForm: React.FC<ConsultantFormProps> = ({
  consultant,
  editConsultant,
  addConsultant,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<ConsultantFormData>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dn: '',
    specialist: true,
    groupConsultant: false,
    sendEmail: true
  });

  useEffect(() => {
    if (consultant) {
      setFormData({
        id: consultant.id ?? '',
        firstName: consultant.firstName,
        lastName: consultant.lastName,
        email: consultant.email,
        phoneNumber: consultant.phoneNumber,
        dn: consultant.dn,
        specialist: consultant.specialist,
        sendEmail: consultant.sendEmail,
        groupConsultant: consultant.groupConsultant
      });
    }
  }, [consultant]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      newErrors.id = 'ID do consultor é obrigatório';
    } else if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(formData.id.trim())) {
      newErrors.id = 'ID inválido. Insira apenas o ID (ex: 74da6d1c-c53f-4639-aa91-dd02778712ea), sem caminhos como "users/"';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Primeiro nome é obrigatório';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Último nome é obrigatório';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.dn.trim()) {
      newErrors.dn = 'DN é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let success = false;
      
      if (consultant?.id) {
        success = await editConsultant(consultant.id, formData);
      } else {
        success = await addConsultant(formData);
      }

      if (success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ConsultantFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };


    const handleChekboxChange = (field: keyof ConsultantFormData, event: any) => {
      const isChecked = event.target.checked
    setFormData(prev => ({
      ...prev,
      [field]: isChecked,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-size-h3">Dados do Consultor</div>
        <div>
          <div>
            <label htmlFor="id" className="block font-medium text-gray-700 mb-2">
              ID do Consultor *
            </label>
            <ClearableInput
              id="id"
              value={formData.id}
              onChange={(value) => handleInputChange('id', value)}
              placeholder="ex: 74da6d1c-c53f-4639-aa91-dd02778712ea"
              disabled={!!consultant?.id}
            />
            {errors.id && (
              <p className="text-color-danger text-size-12 mt-1">{errors.id}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="firstName" className="block font-medium text-gray-700 mb-2">
            Primeiro Nome *
          </label>
          <ClearableInput
            id="firstName"
            value={formData.firstName}
            onChange={(value) => handleInputChange('firstName', value)}
            placeholder="Digite o primeiro nome"
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block font-medium text-gray-700 mb-2">
            Último Nome *
          </label>
          <ClearableInput
            id="lastName"
            value={formData.lastName}
            onChange={(value) => handleInputChange('lastName', value)}
            placeholder="Digite o último nome"
          />
        </div>

        <div>
          <label htmlFor="email" className="block font-medium text-gray-700 mb-2">
            Email
          </label>
          <ClearableInput
            id="email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            placeholder="Digite o email"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block font-medium text-gray-700 mb-2">
            Telefone
          </label>
          <ClearableInput
            id="phone"
            value={formData.phoneNumber}
            onChange={(value) => handleInputChange('phoneNumber', value)}
            placeholder="Digite o telefone"
          />
        </div>

        <div>
          <label htmlFor="dealershipCode" className="block font-medium text-gray-700 mb-2">
            DN *
          </label>
          <ClearableInput
            id="dealershipCode"
            value={formData.dn}
            onChange={(value) => handleInputChange('dn', value)}
            placeholder="Digite o DN"
          />
        </div>
        
        <div>
          <label htmlFor="specialist" className="block font-medium text-gray-700 mb-2">
            Especialista?
          </label>
          <Checkbox
            id="specialist"
            checked={formData.specialist}
            onChange={(event) => {handleChekboxChange('specialist', event)}}
          />
        </div>

        <div>
          <label htmlFor="groupConsultant" className="block font-medium text-gray-700 mb-2">
            Consultor de grupo?
          </label>
          <Checkbox
            id="groupConsultant"
            checked={formData.groupConsultant}
            onChange={(event) => {handleChekboxChange('groupConsultant', event)}}
          />
        </div>

        <div>
          <label htmlFor="sendEmail" className="block font-medium text-gray-700 mb-2">
            Enviar email?
          </label>
          <Checkbox
            id="sendEmail"
            checked={formData.sendEmail}
            onChange={(event) => {handleChekboxChange('sendEmail', event)}}
          />
        </div>
       
      </div> 

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading}
          iconName={loading ? undefined : "rioglyph-check"}
        >
          {loading ? (
            <div className="flex items-center">
              <Spinner />
              {consultant ? 'Salvando...' : 'Criando...'}
            </div>
          ) : (
            consultant ? 'Salvar' : 'Criar'
          )}
        </Button>
      </div>
    </form>
  );
};

export default ConsultantForm;
